import type { FC, ReactNode } from 'react'
import type { GetStaticProps } from 'next'
import type { ArticleSummary } from '@/types'

import Image from 'next/image'
import Link from 'next/link'
import { useState } from 'react'
import { ArrowDown, ArrowUpRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatDate, pad } from '@/lib/format'
import { revealDelay } from '@/lib/motion'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { HeroTitle } from '@/components/generals/hero-title'
import { Mark } from '@/components/generals/mark'
import { RollText } from '@/components/generals/roll-text'
import { SectionLabel } from '@/components/generals/section-label'
import ArticleCard from '@/components/sections/article-card'
import FinalCta from '@/components/sections/final-cta'

import { REVALIDATE_SECONDS, getArticles } from '@/lib/content'

interface InsightsPageProps {
    articles: ArticleSummary[]
}

const InsightsPage: FC<InsightsPageProps> = ({ articles }): ReactNode => {
    const [category, setCategory] = useState('All')

    const featured = articles.find((article) => article.featured) ?? articles[0]
    const categories = ['All', ...Array.from(new Set(articles.map((article) => article.category).filter(Boolean)))]
    const visible = category === 'All' ? articles : articles.filter((article) => article.category === category)

    return (
        <Layout>
            <Seo title='Insights' description='Marketing strategy, creative advertising and performance marketing insights from the BLITZ team.' />

            <section id='insights-hero' aria-labelledby='insights-hero-title' data-nav-theme='light' className='relative overflow-hidden bg-sand text-charcoal'>
                <div className='mx-auto max-w-[1440px] px-5 pb-16 pt-32 md:px-10 md:pb-20 md:pt-40'>
                    <div className='relative grid grid-cols-2 items-center gap-y-3 pb-5 md:grid-cols-3'>
                        <span aria-hidden='true' className='reveal-rule absolute inset-x-0 bottom-0 h-px bg-charcoal/25' style={revealDelay(100)} />
                        <SectionLabel className='reveal-fade'>Insights</SectionLabel>
                        <p className='reveal-fade hidden text-center font-heading text-lg font-extrabold tracking-[-0.03em] md:block' style={revealDelay(150)}>
                            The BLITZ journal
                        </p>
                        <p className='reveal-fade text-right font-mono text-[11px] uppercase tracking-[0.2em] text-charcoal/80' style={revealDelay(200)}>
                            {pad(articles.length)} {articles.length === 1 ? 'article' : 'articles'}
                        </p>
                    </div>

                    <div className='mt-10 grid gap-12 md:mt-14 lg:grid-cols-12 lg:gap-10'>
                        <div className='lg:col-span-7'>
                            <HeroTitle
                                id='insights-hero-title'
                                className='type-hero'
                                delay={220}
                                lines={[
                                    'Ideas behind',
                                    <Mark key='marketing'>better marketing.</Mark>
                                ]}
                            />
                            <p className='reveal-fade mt-7 max-w-md text-[17px] leading-relaxed text-charcoal/85' style={revealDelay(420)}>
                                Practical ideas on brand building, campaign storytelling and turning marketing data into better decisions.
                            </p>
                        </div>

                        {articles.length > 0 && (
                            <nav aria-labelledby='in-this-issue' className='lg:col-span-4 lg:col-start-9 lg:self-end'>
                                <p id='in-this-issue' className='reveal-fade font-mono text-[10px] uppercase tracking-[0.2em] text-charcoal/80' style={revealDelay(380)}>
                                    In this issue
                                </p>
                                <ol className='mt-4 border-t border-charcoal'>
                                    {articles.slice(0, 3).map((article, index) => (
                                        <li key={article.id} className='reveal-fade border-b border-charcoal/25' style={revealDelay(460 + index * 80)}>
                                            <Link
                                                href={`/insights/${article.slug}`}
                                                className='group grid grid-cols-[2rem_1fr_auto] gap-3 py-4 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-coral'
                                            >
                                                <span className='pt-0.5 font-mono text-xs text-charcoal/75'>{pad(index + 1)}</span>
                                                <span>
                                                    <span className='block font-mono text-[10px] uppercase tracking-[0.16em] text-charcoal/80'>{article.category}</span>
                                                    <span className='mt-1 block font-heading text-[17px] font-extrabold leading-snug tracking-[-0.02em] underline-offset-4 group-hover:underline'>
                                                        {article.title}
                                                    </span>
                                                </span>
                                                <ArrowUpRight aria-hidden='true' className='mt-0.5 size-4 shrink-0 transition-transform duration-500 ease-[var(--ease-out-expo)] group-hover:rotate-45' />
                                            </Link>
                                        </li>
                                    ))}
                                </ol>
                            </nav>
                        )}
                    </div>

                    {categories.length > 1 && (
                        <div className='reveal-fade mt-14 flex flex-col gap-4 border-y border-charcoal/20 py-5 md:mt-16 md:flex-row md:items-center md:justify-between' style={revealDelay(700)}>
                            <div className='flex flex-wrap items-center gap-x-6 gap-y-3'>
                                <span className='font-mono text-[10px] uppercase tracking-[0.2em] text-charcoal/75'>Topics</span>
                                <ul className='flex flex-wrap gap-x-6 gap-y-2'>
                                    {categories.slice(1).map((item) => (
                                        <li key={item}>
                                            <a
                                                href='#all-articles'
                                                onClick={() => setCategory(item)}
                                                className='group inline-flex text-[14px] font-semibold focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-coral'
                                            >
                                                <RollText>{item}</RollText>
                                            </a>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <a href='#all-articles' className='group inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-charcoal/80 hover:text-charcoal'>
                                All articles
                                <ArrowDown aria-hidden='true' className='size-4 transition-transform duration-300 group-hover:translate-y-0.5' />
                            </a>
                        </div>
                    )}
                </div>
            </section>

            {featured && (
                <section aria-labelledby='featured-article-title' data-nav-theme='light' className='bg-cream text-charcoal'>
                    <div className='mx-auto max-w-[1440px] px-5 py-20 md:px-10 md:py-28'>
                        <SectionLabel>Featured article</SectionLabel>
                        <Link
                            href={`/insights/${featured.slug}`}
                            className='group mt-10 grid gap-10 rounded-sm focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-coral lg:grid-cols-12 lg:items-end lg:gap-12'
                        >
                            <div className='relative lg:col-span-7'>
                                <div aria-hidden='true' className='absolute -right-3 -top-3 h-2/3 w-1/2 border border-charcoal md:-right-5 md:-top-5' />
                                <div data-reveal-image className='relative aspect-[16/11] overflow-hidden bg-stone'>
                                    {featured.cover && (
                                        <Image src={featured.cover} alt={featured.coverAlt} fill sizes='(min-width: 1024px) 58vw, 100vw' className='object-cover transition-[scale] duration-[1400ms] ease-[var(--ease-out-expo)] group-hover:scale-[1.04]' />
                                    )}
                                </div>
                            </div>
                            <div data-reveal className='lg:col-span-5'>
                                <p className='font-mono text-[11px] uppercase tracking-[0.18em] text-charcoal/80'>
                                    {[featured.category, formatDate(featured.publishedAt), featured.readingTime ? `${featured.readingTime} min read` : ''].filter(Boolean).join(' / ')}
                                </p>
                                <h2 id='featured-article-title' className='mt-5 font-heading text-[clamp(1.9rem,3.2vw,3rem)] font-extrabold leading-[1.04] tracking-[-0.035em] text-balance underline-offset-[6px] group-hover:underline'>
                                    {featured.title}
                                </h2>
                                <p className='mt-5 max-w-md text-[17px] leading-relaxed text-charcoal/85'>{featured.excerpt}</p>
                                <span className='mt-8 inline-flex items-center gap-2 border-b border-charcoal pb-1 text-[13px] font-semibold uppercase tracking-[0.08em]'>
                                    Read article
                                    <ArrowUpRight aria-hidden='true' className='size-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5' />
                                </span>
                            </div>
                        </Link>
                    </div>
                </section>
            )}

            <section id='all-articles' aria-labelledby='all-articles-title' data-nav-theme='light' className='bg-coral text-charcoal'>
                <div className='mx-auto max-w-[1440px] px-5 py-20 md:px-10 md:py-28'>
                    <div className='flex flex-col gap-8 md:flex-row md:items-end md:justify-between'>
                        <div>
                            <SectionLabel className='text-charcoal'>All articles</SectionLabel>
                            <h2 id='all-articles-title' className='mt-6 font-heading type-section'>
                                Latest thinking.
                            </h2>
                        </div>
                        <div role='group' aria-label='Filter articles by category' className='flex flex-wrap gap-2'>
                            {categories.map((item) => (
                                <button
                                    key={item}
                                    type='button'
                                    aria-pressed={category === item}
                                    onClick={() => setCategory(item)}
                                    className={cn(
                                        'h-10 rounded-sm border px-4 text-[12px] font-semibold uppercase tracking-[0.12em] transition-colors duration-300 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-charcoal',
                                        category === item ? 'border-charcoal bg-charcoal text-cream' : 'border-charcoal/40 hover:border-charcoal'
                                    )}
                                >
                                    {item}
                                </button>
                            ))}
                        </div>
                    </div>

                    <p aria-live='polite' className='sr-only'>
                        {visible.length} {visible.length === 1 ? 'article' : 'articles'} shown
                    </p>

                    {visible.length > 0 ? (
                        <div key={category} className='mt-14 grid gap-x-8 gap-y-16 md:grid-cols-2 lg:grid-cols-3'>
                            {visible.map((article) => (
                                <ArticleCard key={article.id} article={article} />
                            ))}
                        </div>
                    ) : (
                        <p className='mt-14 text-charcoal/80'>No articles in this category yet.</p>
                    )}
                </div>
            </section>

            <FinalCta />
        </Layout>
    )
}

export const getStaticProps: GetStaticProps<InsightsPageProps> = async () => {
    const articles = await getArticles()

    return {
        props: { articles },
        revalidate: REVALIDATE_SECONDS
    }
}

export default InsightsPage
